import { motion } from "framer-motion";
import { Package, Layers, Truck } from "lucide-react";

const CHAPTERS = [
  {
    n: "01",
    icon: Package,
    kicker: "Bundle & Save",
    title: "3-Packs of hand-crocheted washcloths.",
    body: "On Poshmark and eBay, our 3-pack bundles unlock premium per-piece discounts while shipping ships as a single flat rate — more cloth, less waste, one box.",
  },
  {
    n: "02",
    icon: Layers,
    kicker: "Boutique Volume",
    title: "5-Packs for the deep restockers.",
    body: "Building a spa drawer or gifting a set? Our 5-pack tier layers on the deepest discount and still travels under a single, flat shipping fee — no per-item surprises.",
  },
  {
    n: "03",
    icon: Truck,
    kicker: "Single Items",
    title: "Oversized Lettermail on eBay singles.",
    body: "For solo cards or a single washcloth, we offer ultra-low-cost, flat-rate Oversized Lettermail shipping — fast, tracked-lite, and priced like a stamp.",
  },
];

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      className="relative bg-[#050505] border-t border-[#262626]"
      data-testid="manifesto-section"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-32">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-16 md:mb-24">
          <div className="md:col-span-4">
            <p className="font-mono-alt text-[11px] tracking-[0.3em] uppercase text-[#c48a64]">
              / 003 — How We Ship
            </p>
          </div>
          <div className="md:col-span-8">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-[5rem] leading-[0.98] font-semibold text-[#f5f5f5] max-w-4xl"
            >
              Get maximum value <br />
              <span className="italic font-medium">by bundling.</span>
            </motion.h2>
            <p className="mt-8 max-w-2xl text-[#a3a3a3] text-base md:text-lg leading-relaxed">
              Our shipping structure is engineered to remove buyer friction. Bundle up,
              save more, and always know the shipping cost before you commit.
            </p>
          </div>
        </div>

        {/* Chapters */}
        <div className="grid grid-cols-1 md:grid-cols-3 border-t border-[#262626]">
          {CHAPTERS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.9,
                  delay: i * 0.12,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className={`relative px-0 md:px-8 py-10 md:py-14 ${
                  i > 0 ? "md:border-l border-[#262626]" : ""
                } ${i > 0 ? "border-t md:border-t-0" : ""}`}
                data-testid={`manifesto-chapter-${c.n}`}
              >
                {/* Huge chapter number backdrop */}
                <span
                  aria-hidden="true"
                  className="absolute -top-2 right-2 md:right-4 font-display text-[22vw] md:text-[10vw] leading-none font-bold text-[#0d0d0d] select-none pointer-events-none"
                >
                  {c.n}
                </span>

                <div className="relative">
                  <div className="flex items-center gap-4 font-mono-alt text-[11px] tracking-[0.28em] uppercase text-[#c48a64]">
                    <span>{c.n}</span>
                    <span className="h-px w-8 bg-[#c48a64]" />
                    <span>{c.kicker}</span>
                  </div>

                  <Icon
                    size={28}
                    strokeWidth={1.25}
                    className="mt-10 text-[#c48a64]"
                    aria-hidden="true"
                  />

                  <h3 className="mt-6 font-display text-2xl md:text-3xl leading-[1.05] font-semibold text-[#f5f5f5] max-w-sm">
                    {c.title}
                  </h3>

                  <p className="mt-5 text-[#a3a3a3] text-[15px] md:text-base leading-relaxed max-w-md">
                    {c.body}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
